import { useEffect, useState } from 'react';
import { Code2, Palette } from 'lucide-react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism-tomorrow.css';
import { kreatorApi } from '../../lib/api';
import type { KreatorSettingsData } from '../KreatorPage';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import { createCodeEditorKeyDown } from '../../utils/codeEditorBehavior';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

export function KreatorCodeEditorSection({ integrationId, settings, onSaved, showError }: Props) {
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [customCss, setCustomCss] = useState(settings?.settings.customCss || '');
  const [customJs, setCustomJs] = useState(settings?.settings.customJs || '');

  useEffect(() => {
    setCustomCss(settings?.settings.customCss || '');
    setCustomJs(settings?.settings.customJs || '');
    setSaveStatus('idle');
  }, [integrationId, settings?.settings.customCss, settings?.settings.customJs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Merge with existing settings to avoid wiping other fields
      const existingSettings = settings?.settings || {
        backgroundColor: '#ffffff',
        fontColor: '#1a1a1a',
        maxContainerWidth: 1440,
        pageLoadingAnimation: 'fade',
        borderStyle: 'rounded' as const,
        borderRadius: 8,
        columnGap: 32,
      };
      await kreatorApi.updateSettings(integrationId, {
        ...existingSettings,
        customCss,
        customJs,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      try {
        await kreatorApi.purgeWpCache(integrationId);
      } catch (purgeErr: unknown) {
        const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
        try {
          const diag = await kreatorApi.diagnoseWp(integrationId);
          showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
        } catch {
          showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const highlightCss = (code: string) =>
    Prism.highlight(code, Prism.languages.css, 'css');

  const highlightJs = (code: string) =>
    Prism.highlight(code, Prism.languages.javascript, 'javascript');

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Code2 className="w-6 h-6 text-accent-primary" />
          Edytor kodu
        </h1>
        <p className="text-text-secondary mt-1">Własny kod CSS i JavaScript dodawany do sklepu</p>
      </div>

      {/* Custom CSS */}
      <div className="card">
        <SectionHeader
          icon={Palette}
          title="Custom CSS"
          description="Reguły CSS, które nadpiszą istniejące style szablonu"
        />
        <div className="rounded-xl border border-border bg-surface-raised overflow-hidden">
          <Editor
            value={customCss}
            onValueChange={setCustomCss}
            onKeyDown={createCodeEditorKeyDown(customCss, setCustomCss)}
            highlight={highlightCss}
            padding={16}
            tabSize={2}
            insertSpaces
            placeholder="/* Napisz swoją pierwszą regułę... */"
            className="ef-code-editor"
            style={{
              fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
              fontSize: 13,
              lineHeight: 1.6,
              minHeight: 200,
            }}
          />
        </div>
      </div>

      {/* Custom JS */}
      <div className="card">
        <SectionHeader
          icon={Code2}
          title="Custom JavaScript"
          description="Kod JavaScript wykonywany po załadowaniu strony"
          accent="warning"
        />
        <div className="rounded-xl border border-border bg-surface-raised overflow-hidden">
          <Editor
            value={customJs}
            onValueChange={setCustomJs}
            onKeyDown={createCodeEditorKeyDown(customJs, setCustomJs)}
            highlight={highlightJs}
            padding={16}
            tabSize={2}
            insertSpaces
            placeholder="// Napisz swój pierwszy skrypt..."
            className="ef-code-editor"
            style={{
              fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
              fontSize: 13,
              lineHeight: 1.6,
              minHeight: 200,
            }}
          />
        </div>
      </div>

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz kod" />
    </div>
  );
}
